import React, { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { ENDPOINTS } from "@/config/endpoints";
import axios from "axios";
import { useRouter } from "next/router";
import esMX from "@/locales/common/esMX";
import enUS from "@/locales/common/enUS";
import { sendOTPForMexico, verifyOTPForMexico } from "@/lib/credlixBypass";
import { MexicoOtpVerification } from "./mexicoOtpVerification";

const schema = (t) => {
  return yup.object({
    Company: yup.string().required(t.MexicoFormFieldsLabels.commonError),
    Email: yup
      .string()
      .required(t.MexicoFormFieldsLabels.commonError)
      .email(t.MexicoFormFieldsLabels.emailNotValid),
    financeAmt: yup.string().required(t.MexicoFormFieldsLabels.commonError),
    CompanyType: yup.string().required(t.MexicoFormFieldsLabels.commonError),
  });
};

const MexicoFormFieldsLabels = () => {
  const router = useRouter();
  const { locale } = router;
  const t = (locale === "es-mx" && esMX) || (locale === "en-us" && enUS);
  const validationSchema = schema(t);
  const [ThankyouRes, setThankyouRes] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [errMSG, setErrMSG] = useState(false);
  const formType = "MexicoForm";
  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
    reset,
    watch,
  } = useForm({
    mode: "all",
    resolver: yupResolver(validationSchema),
  });
  const watchCompany = watch("Company");
  const watchEmail = watch("Email");

  const {
    loader,
    phoneNumber,
    otpBtnDisabled,
    otpRequested,
    showOtpField,
    countdown,
    phoneError,
    otpInvalidFormat,
    networkError,
    verified,
    otpNumericIncorrect,
    setPhoneNumber,
    setVerified,
    handlePhoneInput,
    sendOTP,
    handleOtpChange,
    handleOtpBackspace,
  } = MexicoOtpVerification({
    sendOTPFunc: sendOTPForMexico,
    verifyOTPFunc: verifyOTPForMexico,
    onVerified: () => console.log("OTP verified!"),
    companyName: watchCompany,
    email: watchEmail,
  });

  const formatCountdown = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCaptchaSubmit = useCallback(
    (values) => {
      if (!executeRecaptcha) {
        return false;
      }
      executeRecaptcha().then((gReCaptchaToken) => {
        onFormSubmit({ ...values, phoneNumber, watchEmail,watchCompany }, gReCaptchaToken);
      });
    },
    [executeRecaptcha, phoneNumber,watchEmail,watchCompany]
  );

  async function onFormSubmit(values, gReCaptchaToken) {
    const body = {
      formType: formType,
      company: values.watchCompany,
      email: values.watchEmail,
      phoneNumber: values.phoneNumber,
      financeRequirement: values.financeAmt,
      countryCode: "Mexico (+52)",
      companyType: values.CompanyType,
      sourceUrl: window.location.href,
    };
    let config = {
      method: "post",
      url: ENDPOINTS.MEXICO_CONTACT_US,
      headers: {
        "Content-Type": "application/json",
        "g-recaptcha-response": gReCaptchaToken,
      },
      data: body,
    };

    try {
      const response = await axios(config);
      if (response.status == 200) {
        setThankyouRes(true);
        gtag("event", "getFinanceES", {
          event_category: "Get financed ",
          event_label: "Get financed ",
        });
        window.dataLayer.push({
          event: "Mexicoconversion",
          Conversion_ID: "16468312892",
          Conversion_labels: "MIX0CL7dyt0ZELyG2qw9",
        });

        setTimeout(() => {
          reset();
          router.push("/thankyou-response");
          setThankyouRes(false);
          setPhoneNumber("");
          setVerified(false);
        }, 5000);
      }
    } catch (err) {
      setErrMSG(true);
      console.log("error", err);
      setTimeout(() => {
        reset();
      }, 5000);
    }
  }

  const isSubmitDisabled = !(
    isValid &&
    phoneNumber &&
    verified && watchCompany && watchEmail
  );

  return (
    <div className="bg-white rounded-[15px] pt-3 pb-5 sm:py-4 px-2 sm:px-1 lg:w-full lg:max-w-[457px] md:max-w-[420px] mx-auto md:relative md:m-auto md:mb-[20px]">
      <h3 className="text-base2 sm:text-base text-secondary mb-1 mt-1">
        {t.MexicoFormFieldsLabels.heading1}
        <span className="text-brand font-medium">
          {" "}
          {t.MexicoFormFieldsLabels.heading2}
        </span>
      </h3>

      <div className="border border-bordercolor rounded-md p-1 sm:p-1">
        <form onSubmit={handleSubmit(handleCaptchaSubmit)} className="w-full">
          <div className="part-1">
            <div className="">
              <label className="text-sm font-medium text-secondary mt-1">
                {t.MexicoFormFieldsLabels.companyLabel}
              </label>
              <input
                {...register("Company")}
                type="text"
                id="company"
                placeholder={t.MexicoFormFieldsLabels.companyPlaceholder}
                className="block w-full min-w-0 text-sm flex-1 pl-1 rounded-md border-0 py-1 ring-1 ring-inset ring-bordercolor focus:ring-[#555] focus:outline-none mt-0.5"
              />
              <p className="text-progressColor4 text-xs mt-0.5">
                {errors.Company?.message}
              </p>
            </div>
            {/* Company Name End*/}
            <div className="flex items-start gap-x-xsp w-full">
              <div className="w-[38%] mt-1">
                <label className="text-sm font-medium text-secondary block mb-[1px]">
                  {t.MexicoFormFieldsLabels.countrycodeLabel}
                </label>
                <input
                  value={t.MexicoFormFieldsLabels.countrycodeData}
                  type="text"
                  id="company"
                  readOnly
                  className="block w-full min-w-0 text-sm flex-1 pl-1 rounded-md border-0 py-1 ring-1 ring-inset ring-bordercolor focus:ring-[#555] focus:outline-none mt-0.5"
                />
              </div>
              <div className="w-[61%] mt-1">
                <label className="text-sm font-medium block text-secondary">
                  {t.MexicoFormFieldsLabels.phoneLabel}
                </label>
                <input
                  value={phoneNumber}
                  onChange={(e) => handlePhoneInput(e.target.value)}
                  maxLength={10}
                  id="tel"
                  type="tel"
                  placeholder={t.MexicoFormFieldsLabels.phonePlaceholder}
                  className="block w-full min-w-0 text-sm flex-1 pl-1 sm:text-[13px] rounded-md border-0 py-1 ring-1 ring-inset ring-bordercolor focus:ring-[#555] focus:outline-none mt-0.5"
                />
                {phoneError && (
                  <p className="text-progressColor4 text-xs mt-[3px]">
                    {t.MexicoFormFieldsLabels.phoneInvalid}
                  </p>
                )}
              </div>
            </div>

            {/* Email Start */}
            <div className="mt-2">
              <label className="text-sm font-medium block text-secondary">
                {t.MexicoFormFieldsLabels.emailLabel}
              </label>
              <div className="flex relative">
                <input
                  {...register("Email")}
                  id="email"
                  type="email"
                  placeholder={t.MexicoFormFieldsLabels.emailPlaceholder}
                  className="block w-full text-sm pl-1 h-6 rounded-md border-0 py-1.5 ring-1 ring-inset ring-bordercolor focus:border-[#555] focus:ring-[#555] focus:outline-none"
                />

                {verified ? (
                  <div className="absolute flex items-center right-0 z-10 bg-[#4A9447] px-xsp text-xs text-white h-8 rounded-[4px] my-[4px] mr-xsp">
                   {t.miSupplier.verified}
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={otpBtnDisabled || loader}
                    onClick={sendOTP}
                    className={`absolute right-0 top-0 bottom-0 rounded-r-md text-sm px-xsp text-center ${
                      otpBtnDisabled || loader
                        ? "bg-[#ddd] text-[#888] cursor-not-allowed rounded-[4px] my-[4px] mr-xsp h-8"
                        : "border border-[#086AC9] text-[#086AC9] rounded-[4px] my-[4px] mr-xsp h-8"
                    }`}
                  >
                    {loader ? t.miSupplier.verifying: t.miSupplier.verify}
                  </button>
                )}
              </div>
              <p className="text-progressColor4 text-xs mt-0.5">
                {errors.Email?.message}
              </p>
              {networkError && (
                <p className="text-progressColor4 text-xs mt-[3px]">
                 {t.miSupplier.captchaIssue}
                </p>
              )}

              {otpRequested && !verified && (
                <div className="flex justify-end text-sm mt-1">
                  <span className="text-brand mr-1">{t.miSupplier.getOtp}</span>{t.miSupplier.resend}{" "}{formatCountdown(countdown)}s
                </div>
              )}

              {!loader && showOtpField && (
                <>
                  <label
                    className="text-sm font-medium text-secondary mb-0 mt-1 block"
                    htmlFor="digit1"
                  >
                   {t.miSupplier.enterOtp}
                  </label>
                  <div className="flex flex-wrap justify-between mt-[5px]">
                    {[...Array(6)].map((_, index) => (
                      <input
                        key={index}
                        id={`digit${index + 1}`}
                        type="text"
                        maxLength={1}
                        pattern="[0-9]"
                        className="h-[40px] px-1 text-xs text-center rounded-md border border-[#aaa] min-w-0 w-[50px] sm:w-[40px]"
                        onChange={(e) => handleOtpChange(e, index)}
                        onKeyDown={(e) => handleOtpBackspace(e, index)}
                      />
                    ))}
                  </div>

                  {otpInvalidFormat && (
                    <p className="text-progressColor4 text-xs mt-[3px]">
                     {t.miSupplier.correctOtp}
                    </p>
                  )}
                  {otpNumericIncorrect && (
                    <p className="text-progressColor4 text-xs mt-[3px]">
                     {t.miSupplier.incorrectOtp}
                    </p>
                  )}
                </>
              )}
            </div>
            {/* Email End  */}

            {/* Company Type starts*/}
            <div className="mt-1">
              <label className="text-sm font-medium text-secondary">
                {t.MexicoFormFieldsLabels.companytypeLabel}
              </label>
              <input
                {...register("CompanyType")}
                type="text"
                id="company"
                placeholder={t.MexicoFormFieldsLabels.companytypePlaceholder}
                className="block w-full min-w-0 text-sm flex-1 pl-1 rounded-md border-0 py-1 ring-1 ring-inset ring-bordercolor focus:ring-[#555] focus:outline-none"
              />
              {errors.CompanyType && (
                <p className="text-progressColor4 text-xs mt-0.5">
                  {errors.CompanyType?.message}
                </p>
              )}
            </div>
            <div className="mt-1">
              <label className="text-sm font-medium text-secondary">
                {t.MexicoFormFieldsLabels.financeLabel}
              </label>
              <div className="flex rounded-md  mt-0.5">
                <input
                  value="USD"
                  name="currency"
                  readOnly
                  className="inline-flex w-[70px] justify-center text-sm items-center  rounded-l-md border border-bordercolor border-r-0 px-2 text-gray-500"
                />
                <input
                  {...register("financeAmt")}
                  id="financeAmt"
                  type="tel"
                  placeholder={t.MexicoFormFieldsLabels.financePlaceholder}
                  className="block w-full min-w-0 text-sm flex-1 pl-1 rounded-none  rounded-r-md border-0 py-1 ring-1 ring-inset ring-bordercolor focus:ring-[#555] focus:outline-none"
                />
              </div>
              <p className="text-progressColor4 text-xs mt-0.5">
                {errors.financeAmt?.message}
              </p>
            </div>
          </div>
          <div className="mt-2">
            <button
              disabled={isSubmitDisabled}
              className={`py-2 sm:py-1 px-3 rounded-md text-sm w-full ${
                isSubmitDisabled
                  ? "bg-[#D9D9D9] text-[#6F6F6F] font-medium cursor-not-allowed shadow-custom"
                  : "bg-gradient-to-r from-[#022B5C] to-[#0363C4] hover:from-[#0363C4] hover:to-[#022B5C] font-medium shadow-lg text-white"
              }`}
              type="submit"
            >
              {t.MexicoFormFieldsLabels.submit}
            </button>
          </div>
        </form>
        {ThankyouRes ? (
          <>
            <div className="text-green text-center">
              <p className="text-sm font-medium mt-3">
                {t.MexicoFormFieldsLabels.successMsg}
              </p>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default MexicoFormFieldsLabels;
